/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Form, message } from "antd";
import { AgentApi } from "api/agent";
import { RegisterAgentForm } from "api/agent/type";
import { ReactComponent as StarRed } from "assets/common/icons/star-solid.svg";
import ModalNotiMobile from "components/common/Modal/ModalNotiMobile";
import { LIST_BACKGROUND_CAPTCHA } from "constants/captcha";
import { REGEX_EMOJI, REGEX_PASSWORD, REGEX_USERNAME } from "constants/regex";
import { ResponseCode } from "constants/response";
import { ModalType } from "enums/modalType";
import { useAppDispatch } from "hooks/app-hook";
import { ReactNode, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { setOpenModalNotiMobile } from "store/master";
import { ApiResponse } from "types/api";
import * as yup from "yup";

import MarqueeCustom from "../HomePage/Marquee";

const schema = yup.object({
  bank: yup.string().trim().required("Vui lòng nhập tên ngân hàng của bạn"),
  fullname: yup.string().trim().required("Vui lòng nhập tên của bạn"),
  branch: yup
    .string()
    .trim()
    .required("Vui lòng nhập chi nhánh ngân hàng của bạn"),
  mobile: yup
    .string()
    .trim()
    .required("Vui lòng nhập số điện thoại di động của bạn"),
  account_number: yup
    .string()
    .trim()
    .required("Vui lòng nhập số tài khoản của bạn"),
  account: yup
    .string()
    .trim()
    .lowercase()
    .required("Vui lòng nhập tài khoản")
    .test("emoji", "Không nhập emoji", (username: string): boolean => {
      if (!username) return false;
      const regex = new RegExp(REGEX_EMOJI);
      return !regex.test(username);
    })
    .matches(
      REGEX_USERNAME,
      `Tài khoản phải lớn hơn 6 ký tự,bắt đầu phải bằng chữ cái,không điền các ký tự đặc biệt.`,
    )
    .required("Vui lòng nhập tên tài khoản"),
  password: yup
    .string()
    .trim()
    .matches(
      REGEX_PASSWORD,
      "Mật khẩu phải hơn 6 ký tự phải bao gồm chữ cái và số",
    )
    .required("Vui lòng nhập mật khẩu"),
  confirm_password: yup
    .string()
    .trim()
    .matches(
      REGEX_PASSWORD,
      "Xác nhận mật khẩu phải hơn 6 ký tự phải bao gồm chữ cái và số",
    )
    .required("Vui lòng xác nhận lại mật khẩu của bạn")
    .test(
      "empty",
      "Xác nhận mật khẩu không khớp",
      (
        confirm_password: string | undefined,
        testContext: yup.TestContext<yup.AnyObject>,
      ): boolean => {
        if (!testContext.parent.password) return true;
        return testContext.parent.password === confirm_password;
      },
    ),
  email: yup
    .string()
    .email("Địa chỉ email không hợp lệ")
    .trim()
    .required("Vui lòng nhập địa chỉ Email"),
  captcha: yup
    .string()
    .trim()
    .required("Mã xác nhận không được bỏ trống, xin lấy mã xác nhận"),
});

interface State {
  valueCaptcha: string;
  numberBg: number;
  title: string;
}

interface RegisterAgentFormExtend extends RegisterAgentForm {
  captcha: string;
}

const AgentApplyPage = () => {
  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    reset,
  } = useForm<RegisterAgentFormExtend>({
    resolver: yupResolver(schema),
    mode: "all",
  });

  const dispatch = useAppDispatch();

  const [state, setState] = useState<State>({
    valueCaptcha: "",
    numberBg: 0,
    title: "",
  });

  const [loading, setIsLoading] = useState<boolean>(false);

  const onSubmit = async (data: RegisterAgentFormExtend) => {
    const { captcha, ...dataForm } = data;

    if (state.valueCaptcha !== captcha) {
      setState((prev) => ({
        ...prev,
        title: "Lỗi mã xác minh hoặc lỗi đầu vào, vui lòng quay lại",
      }));
      return;
    }

    setState((prev) => ({
      ...prev,
      title: "",
    }));

    setIsLoading(true);
    const res = await AgentApi.AgentRegister(dataForm);
    setIsLoading(false);
    const resData = res.data as ApiResponse;

    if (resData.status !== ResponseCode.SUCCESS) {
      message.error({
        key: "register",
        content: resData.msg,
      });
      return;
    }

    message.success({
      key: "register",
      content: resData.msg,
    });
    reset();
  };

  const generateRandomNumbers = () => {
    const newRandomNumbers: number[] = [];

    while (newRandomNumbers.length < 4) {
      const randomNumber = Math.floor(Math.random() * 10);
      newRandomNumbers.push(randomNumber);
    }

    setState((prev) => ({
      ...prev,
      numberBg: Math.floor(Math.random() * 9),
      valueCaptcha: newRandomNumbers.join(""),
    }));
  };

  const renderTitle = () => {
    if (errors.account) {
      setState((prev) => ({
        ...prev,
        title: errors.account?.message as string,
      }));
      return;
    }

    if (errors.password) {
      setState((prev) => ({
        ...prev,
        title: errors.password?.message as string,
      }));
      return;
    }

    if (errors.confirm_password) {
      setState((prev) => ({
        ...prev,
        title: errors.confirm_password?.message as string,
      }));
      return;
    }

    if (errors.fullname) {
      setState((prev) => ({
        ...prev,
        title: errors.fullname?.message as string,
      }));
      return;
    }

    if (errors.email) {
      setState((prev) => ({
        ...prev,
        title: errors.email?.message as string,
      }));
      return;
    }

    if (errors.mobile) {
      setState((prev) => ({
        ...prev,
        title: errors.mobile?.message as string,
      }));
      return;
    }

    if (errors.bank) {
      setState((prev) => ({
        ...prev,
        title: errors.bank?.message as string,
      }));
      return;
    }

    if (errors.account_number) {
      setState((prev) => ({
        ...prev,
        title: errors.account_number?.message as string,
      }));
      return;
    }

    if (errors.branch) {
      setState((prev) => ({
        ...prev,
        title: errors.branch?.message as string,
      }));
      return;
    }

    if (errors.captcha) {
      setState((prev) => ({
        ...prev,
        title: errors.captcha?.message as string,
      }));
      return;
    }
  };

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <MarqueeCustom />

      <div
        css={css`
          height: 70px;
          font-size: 22px;
          line-height: 70px;
          letter-spacing: 2px;
          text-align: center;
          background-color: #2d7699;
          color: white;
        `}
      >
        Đăng ký đại lý
      </div>

      <Form
        onSubmitCapture={handleSubmit(onSubmit)}
        css={css`
          input,
          select {
            flex: 1;
            padding: 15px;

            display: block;
            width: 100%;
            height: 34px;
            padding: 6px 12px;
            font-size: 14px;
            line-height: 1.42857143;
            color: #555555;
            background-color: #fff;
            background-image: none;
            border: 1px solid #e96214;
            border-radius: 10px;
            box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075);
            transition:
              border-color ease-in-out 0.15s,
              box-shadow ease-in-out 0.15s;
          }
        `}
      >
        <div
          css={css`
            padding: 24px 40px;
          `}
        >
          <RenderField
            control={control}
            label="Tài khoản đại lý"
            name="account"
            placeholder="Từ 2-15 kí tự, phải bắt đầu bằng chữ cái, có thể gồm cả chữ cái, số và gạch dưới."
          />

          <RenderField
            control={control}
            label="Mật khẩu đại lý"
            name="password"
            type="password"
            placeholder="Vui lòng nhập mật khẩu"
          />

          <RenderField
            control={control}
            label="Xác nhận mật khẩu"
            name="confirm_password"
            type="password"
            placeholder="Vui lòng xác nhận lại mật khẩu"
          />

          <RenderField
            control={control}
            label="Họ và tên thật"
            name="fullname"
            placeholder="Cần giống tên tài khoản ngân hàng, nếu không thì không thể rút tiền"
          />

          <RenderField
            control={control}
            label="Số điện thoại"
            name="mobile"
            type="number"
            placeholder="Vui lòng nhập số điện thoại"
          />

          <RenderField
            control={control}
            label="Địa chỉ email"
            name="email"
            type="email"
            placeholder="Vui lòng nhập địa chỉ email của bạn"
          />

          <div
            css={css`
              display: flex;
              flex-direction: column;
              margin-bottom: 12px;
            `}
          >
            <div
              css={css`
                display: flex;
                gap: 4px;
                flex-direction: column;
              `}
            >
              <div
                css={css`
                  display: flex;
                  gap: 4px;
                  align-items: center;
                  padding-left: 4px;
                `}
              >
                <div>
                  <StarRed height={14} width={14} fill="red" />
                </div>
                <p
                  css={css`
                    font-size: 13px;
                    color: #808080;
                  `}
                >
                  Ngân hàng
                </p>
              </div>
              <Controller
                name="bank"
                defaultValue=""
                control={control}
                render={({ field: { onChange, value } }) => (
                  <select onChange={onChange} value={value}>
                    <option value="">vui lòng chọn ngân hàng</option>
                    <option value={"ABBANK"} label="ABBANK">
                      ABBANK
                    </option>
                    <option value={"ACB BANK"} label="ACB BANK">
                      ACB BANK
                    </option>
                    <option value={"AGRIBANK"} label="AGRIBANK">
                      AGRIBANK
                    </option>
                    <option value={"ANZVL"} label="ANZVL">
                      ANZVL
                    </option>
                    <option value={"BAC A BANK"} label="BAC A BANK">
                      BAC A BANK
                    </option>
                    <option value={"BAO VIET BANK"} label="BAO VIET BANK">
                      BAO VIET BANK
                    </option>
                    <option value={"BIDV BANK"} label="BIDV BANK">
                      BIDV BANK
                    </option>
                    <option value={"CAKE"} label="CAKE">
                      CAKE
                    </option>
                    <option
                      value={"CBBank"}
                      label="NH TM TNHH MTV XAY DUNG VIET NAM"
                    >
                      NH TM TNHH MTV XAY DUNG VIET NAM
                    </option>
                    <option value={"CIMB"} label="NH MTV CIMB">
                      NH MTV CIMB
                    </option>
                    <option value={"OPBANK"} label="CO OPBANK">
                      CO OPBANK
                    </option>
                    <option value={"DONGA BANK"} label="DONGA BANK">
                      DONGA BANK
                    </option>
                    <option value={"EXIMBANK"} label="EXIMBANK">
                      EXIMBANK
                    </option>
                    <option value={"GP BANK"} label="GP BANK">
                      GP BANK
                    </option>
                    <option value={"HD BANK"} label="HD BANK">
                      HD BANK
                    </option>
                    <option value={"HONGLEONG BANK"} label="HONGLEONG BANK">
                      HONGLEONG BANK
                    </option>
                    <option
                      value={"HSBC"}
                      label="HSBC - THE HONGKONG AND SHANGHAI BANKING CORPORATION LIMITED"
                    >
                      HSBC - THE HONGKONG AND SHANGHAI BANKING CORPORATION
                      LIMITED
                    </option>
                    <option value={"INDOVINA BANK"} label="INDOVINA BANK">
                      INDOVINA BANK
                    </option>
                    <option value={"KBANK"} label="KBANK">
                      KBANK
                    </option>
                    <option value={"KIENLONGBANK"} label="KIENLONGBANK">
                      KIENLONGBANK
                    </option>
                    <option value={"LIENVIET BANK"} label="LIENVIET BANK">
                      LIENVIET BANK
                    </option>
                    <option value={"LIOBANK"} label="LIOBANK">
                      LIOBANK
                    </option>
                    <option value={"MBBANK"} label="MBBANK">
                      MBBANK
                    </option>
                    <option value={"MSB BANK"} label="MSB BANK">
                      MSB BANK
                    </option>
                    <option value={"NAMA BANK"} label="NAMA BANK">
                      NAMA BANK
                    </option>
                    <option value={"SCB"} label="NH TMCP QUOC DAN">
                      NH TMCP QUOC DAN
                    </option>
                    <option value={"OCB"} label="OCB BANK">
                      OCB BANK
                    </option>
                    <option value={"OCEANBANK"} label="OCEANBANK">
                      OCEANBANK
                    </option>
                    <option value={"PGBANK"} label="PGBANK">
                      PGBANK
                    </option>
                    <option value={"PVCOMBANK"} label="PVCOMBANK">
                      PVCOMBANK
                    </option>
                    <option value={"SACOMBANK"} label="SACOMBANK">
                      SACOMBANK
                    </option>
                    <option value={"SAIGONBANK"} label="SAIGONBANK">
                      SAIGONBANK
                    </option>
                    {/* <option value={"ABBANK"} label="SCB - The Siam Commercial Bank">
                SCB - The Siam Commercial Bank
              </option> */}
                    <option value={"SCBVL"} label="SCBVL">
                      SCBVL
                    </option>
                    <option value={"SEABANK"} label="SEABANK">
                      SEABANK
                    </option>
                    <option value={"SHB BANK"} label="SHB BANK">
                      SHB BANK
                    </option>
                    <option value={"SHINHAN BANK VN"} label="SHINHAN BANK VN">
                      SHINHAN BANK VN
                    </option>
                    <option value={"TECHCOMBANK"} label="TECHCOMBANK">
                      TECHCOMBANK
                    </option>
                    <option value={"TIMO BANK"} label="TIMO BANK">
                      TIMO BANK
                    </option>
                    <option value={"TPBANK"} label="TPBANK">
                      TPBANK
                    </option>
                    <option value={"UBANK"} label="UBANK">
                      UBANK
                    </option>
                    <option
                      value={"UNITED OVERSEAS BANK"}
                      label="UNITED OVERSEAS BANK"
                    >
                      UNITED OVERSEAS BANK
                    </option>
                    <option
                      value={"VIETNAM DEVELOPMENT BANK"}
                      label="VIETNAM DEVELOPMENT BANK"
                    >
                      VIETNAM DEVELOPMENT BANK
                    </option>
                    <option value={"VIB BANK"} label="VIB BANK">
                      VIB BANK
                    </option>
                    <option value={"VIDPUBLIC BANK"} label="VIDPUBLIC BANK">
                      VIDPUBLIC BANK
                    </option>
                    <option value={"NHTMCP BAN VIET"} label="NHTMCP BAN VIET">
                      NHTMCP BAN VIET
                    </option>
                    <option value={"VIETCAPITAL"} label="VIETCAPITAL">
                      VIETCAPITAL
                    </option>
                    <option value={"VIETA BANK"} label="VIETA BANK">
                      VIETA BANK
                    </option>
                    <option value={"VIETBANK"} label="VIETBANK">
                      VIETBANK
                    </option>
                    <option value={"VIETCOMBANK"} label="VIETCOMBANK">
                      VIETCOMBANK
                    </option>
                    <option value={"VIETINBANK"} label="VIETINBANK">
                      VIETINBANK
                    </option>
                    <option
                      value={"VIETNAM BANK FOR SOCIAL POLICIES"}
                      label="VIETNAM BANK FOR SOCIAL POLICIES"
                    >
                      VIETNAM BANK FOR SOCIAL POLICIES
                    </option>
                    <option value={"VP DIRECT"} label="VP DIRECT">
                      VP DIRECT
                    </option>
                    <option value={"VPBANK"} label="VPBANK">
                      VPBANK
                    </option>
                    <option value={"NH LD VIET NGA"} label="NH LD VIET NGA">
                      NH LD VIET NGA
                    </option>
                    <option value={"WOORI BANK"} label="WOORI BANK">
                      WOORI BANK
                    </option>
                    <option value={"Other"} label="Other">
                      Other
                    </option>
                  </select>
                )}
              />
            </div>
          </div>
          <RenderField
            control={control}
            label="Số tài khoản"
            name="account_number"
            type="number"
            placeholder="ví dụ：6227002020690175526"
          />

          <RenderField
            control={control}
            label="Chi nhánh ngân hàng"
            name="branch"
            placeholder="ví dụ : thành phố hồ chí minh"
          />

          <RenderField
            control={control}
            label="Chi nhánh ngân hàng"
            name="captcha"
            placeholder="Mã xác minh"
            children={
              <Controller
                name={"captcha"}
                defaultValue=""
                control={control}
                render={({ field: { onChange, value } }) => (
                  <div
                    css={css`
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      padding: 6px 12px;
                      width: 100%;
                      height: 100%;
                      font-size: 14px;
                      line-height: 1.42857143;
                      color: #555555;
                      background-color: #fff;
                      background-image: none;
                      border: 1px solid #e96214;
                      border-radius: 10px;
                      box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075);
                      transition:
                        border-color ease-in-out 0.15s,
                        box-shadow ease-in-out 0.15s;
                      input {
                        border-color: transparent;
                        box-shadow: none;
                        background-color: white;
                        padding: 0;
                        height: initial;
                        :focus-visible {
                          outline: none;
                          box-shadow: none;
                          background-color: white !important;
                        }
                      }
                    `}
                  >
                    <input
                      type={"text"}
                      value={value}
                      placeholder={"Nhập mã xác minh"}
                      onChange={onChange}
                      onClick={generateRandomNumbers}
                    />

                    {state.valueCaptcha && (
                      <div
                        style={{
                          backgroundImage: `url(${
                            LIST_BACKGROUND_CAPTCHA[state.numberBg]
                          })`,
                        }}
                        css={css`
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          width: 60px;
                          height: 22px;
                          margin-right: 10px;

                          background-size: cover;
                          background-repeat: no-repeat;
                          background-position: center;
                          cursor: pointer;

                          span {
                            font-size: 20px;
                            opacity: 0.6;
                            font-style: italic;
                          }
                        `}
                      >
                        <span>{state.valueCaptcha}</span>
                      </div>
                    )}
                  </div>
                )}
              />
            }
          />

          <button
            css={css`
              margin-top: 12px;
              margin-bottom: 20px;
              padding: 4px;
              width: 100%;
              font-size: 20px;
              border-radius: 0.375rem;
              text-align: center;
              background-color: #7bbfd4;
              color: white;
              border-color: transparent;
              height: 2.5rem;
              opacity: ${isValid ? 1 : 0.5};
            `}
            type="submit"
            onClick={() => {
              renderTitle();
              dispatch(setOpenModalNotiMobile(ModalType.NOTI_MOBILE));
            }}
            disabled={loading}
          >
            Lập tức đăng ký
          </button>
        </div>
      </Form>

      {!!state.title && <ModalNotiMobile title={state.title} />}
    </div>
  );
};

export default AgentApplyPage;

interface PropsField {
  label: string;
  control: any;
  name: string;
  type?: string;
  placeholder: string;
  children?: ReactNode;
}
const RenderField = (props: PropsField) => {
  const { label, control, name, type = "text", placeholder, children } = props;
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        margin-bottom: 12px;
      `}
    >
      <div
        css={css`
          display: flex;
          gap: 4px;
          flex-direction: column;
        `}
      >
        <div
          css={css`
            display: flex;
            gap: 4px;
            align-items: center;
            padding-left: 4px;
          `}
        >
          <div>
            <StarRed height={14} width={14} fill="red" />
          </div>
          <p
            css={css`
              font-size: 13px;
              color: #808080;
            `}
          >
            {label}
          </p>
        </div>

        {children ? (
          children
        ) : (
          <Controller
            name={name}
            defaultValue=""
            control={control}
            render={({ field: { onChange, value } }) => (
              <>
                <input
                  type={type}
                  value={value}
                  placeholder={placeholder}
                  onChange={onChange}
                />
              </>
            )}
          />
        )}
      </div>
    </div>
  );
};
